import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import AddIcon from '@mui/icons-material/Add'
import AddLocationIcon from '@mui/icons-material/AddLocation'
import Campaign from '@mui/icons-material/Campaign'
import Search from '@mui/icons-material/Search'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import API from '../../../services/API'
import BigModal from '../../../components/Modals/BigModal'
import AddNewClient from '../../clients/AddNewClient'
import AnnouncePortalDialog from './AnnouncePortalDialog'
import { GlobalContext } from '../../../App'
import { currencySymbol } from '../../../helpers/region'
import { Paper } from '@mui/material'

const BAR_HUE = '#0F766E'

const StatTile = ({ label, value, caption }) => (
  <Box display="flex" flex={1} height="100%">
    <Card variant="outlined" sx={{ p: 1.5, width: '100%' }}>
      <Typography variant="body2" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Card>
  </Box>
)

// Eight weekly bars of journey activity — single hue, no axes; the number
// beside it carries the exact value (sparklines indicate shape, not scale).
const ActivitySparkline = ({ journeysByWeek = [] }) => {
  const max = Math.max(...journeysByWeek, 1)
  return (
    <Stack direction="row" spacing={0.4} alignItems="flex-end" height={28}>
      {journeysByWeek.map((count, index) => (
        <Box
          key={index}
          sx={{
            width: 7,
            height: `${Math.max(8, (count / max) * 100)}%`,
            borderRadius: 0.5,
            backgroundColor: count ? BAR_HUE : 'action.hover',
          }}
        />
      ))}
    </Stack>
  )
}

const getPortalAdoption = (org) => {
  const { total, active30d } = org.portalUsers || {}
  if (!total) {
    return org.journeys30d > 0
      ? { label: 'No portal users', color: 'error' }
      : { label: 'No portal users', color: 'default' }
  }
  if (org.journeys30d > 0 && active30d === 0) {
    return { label: `0/${total} users active`, color: 'error' }
  }
  return {
    label: `${active30d}/${total} users active`,
    color: active30d > 0 ? 'success' : 'default',
  }
}

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('')

const OrganisationCard = ({ org, currency, onAnnounce }) => {
  const navigate = useNavigate()
  const adoption = getPortalAdoption(org)

  return (
    <Card
      variant="outlined"
      sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}
    >
      <Stack spacing={1.25} flex={1}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            sx={{
              bgcolor: org.color || 'anBlue.main',
              fontWeight: 700,
              width: 44,
              height: 44,
            }}
          >
            {getInitials(org.orgName)}
          </Avatar>
          <Box minWidth={0} flex={1}>
            <Typography sx={{ fontWeight: 800 }} noWrap>
              {org.orgName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {[
                org.baseLocation?.name,
                org.town,
                `${org.wardCount} ward${org.wardCount === 1 ? '' : 's'}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
          {org.archived && (
            <Chip size="small" label="Archived" sx={{ borderRadius: 1 }} />
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={2.5}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Box>
              <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {org.journeys30d}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                journeys / 30d
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {org.bookings30d > 0
                  ? `${Math.round((org.portalBookings30d / org.bookings30d) * 100)}%`
                  : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                via portal
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {org.portalUsers?.total || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                portal users
              </Typography>
            </Box>
          </Stack>
          <ActivitySparkline journeysByWeek={org.journeysByWeek} />
        </Stack>

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {(org.portalUsers?.total > 0 || adoption.color === 'error') && (
            <Chip
              size="small"
              label={adoption.label}
              color={adoption.color}
              variant={adoption.color === 'default' ? 'outlined' : 'filled'}
              sx={{ borderRadius: 1, fontWeight: 700 }}
            />
          )}
          {org.invoices.outstandingTotal > 0 && (
            <Chip
              size="small"
              label={`${currency}${org.invoices.outstandingTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })} outstanding${org.invoices.overdueCount ? ` · ${org.invoices.overdueCount} overdue` : ''}`}
              color={org.invoices.overdueCount ? 'error' : 'default'}
              variant={org.invoices.overdueCount ? 'filled' : 'outlined'}
              sx={{ borderRadius: 1, fontWeight: 700 }}
            />
          )}
          {org.unplannedJourneys > 0 && (
            <Chip
              size="small"
              label={`${org.unplannedJourneys} to plan`}
              color="warning"
              sx={{ borderRadius: 1, fontWeight: 700 }}
            />
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ mt: 'auto' }}
        >
          <Button
            size="small"
            variant="outlined"
            color="contrast"
            onClick={() => navigate(`/clients/${org._id}`)}
          >
            Open
          </Button>
          <Box flex={1} />
          <Tooltip title="Preview their hospital portal">
            <IconButton
              size="small"
              onClick={() =>
                window.open(`/pts/hospital-portal-preview/${org._id}`)
              }
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="New booking">
            <IconButton
              size="small"
              onClick={() => navigate('/pts/bookings/new-booking')}
            >
              <AddLocationIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Email ward contacts an introduction to the booking portal">
            <IconButton size="small" onClick={() => onAnnounce(org)}>
              <Campaign fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  )
}

const Organisations = () => {
  const { tenantInfo } = useContext(GlobalContext)
  const currency = currencySymbol(tenantInfo?.region)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [dashboard, setDashboard] = useState(null)
  const [openNew, setOpenAddNew] = useState(false)
  const [includeArchived, setIncludeArchived] = useState(false)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('activity')
  const [announceOrg, setAnnounceOrg] = useState(null)

  const loadDashboard = () => {
    setLoading(true)
    setFetchError(false)
    API.get(
      `/pts/organisations/dashboard${includeArchived ? '?includeArchived' : ''}`,
    )
      .then((res) => {
        if (res.status === 200) {
          setDashboard(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch((e) => {
        console.error(e)
        setFetchError(true)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadDashboard()
  }, [includeArchived])

  const totals = dashboard?.totals
  const organisations = useMemo(() => {
    const query = search.trim().toLowerCase()
    const filtered = (dashboard?.organisations || []).filter(
      (org) =>
        !query ||
        org.orgName?.toLowerCase().includes(query) ||
        org.town?.toLowerCase().includes(query) ||
        org.baseLocation?.name?.toLowerCase().includes(query),
    )
    const sorters = {
      activity: (a, b) => b.journeys30d - a.journeys30d,
      name: (a, b) => (a.orgName || '').localeCompare(b.orgName || ''),
      outstanding: (a, b) =>
        b.invoices.outstandingTotal - a.invoices.outstandingTotal,
      unplanned: (a, b) => b.unplannedJourneys - a.unplannedJourneys,
    }
    return [...filtered].sort(sorters[sortBy] || sorters.activity)
  }, [dashboard, search, sortBy])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          gap={1}
        >
          <Box>
            <Typography variant="title2">Organisations</Typography>
            <Typography variant="body2" color="text.secondary">
              Hospitals and customers you provide patient transport for.
            </Typography>
          </Box>
          <Stack direction="row" gap={1} alignItems="center">
            <Button
              size="small"
              variant="text"
              color="inherit"
              startIcon={includeArchived ? <Visibility /> : <VisibilityOff />}
              onClick={() => setIncludeArchived((prev) => !prev)}
            >
              {includeArchived ? 'Hide archived' : 'Show archived'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddNew(true)}
            >
              Add Organisation
            </Button>
          </Stack>
        </Stack>
      </Grid>

      {loading && (
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      )}
      {fetchError && (
        <Grid item xs={12}>
          <Alert severity="error" variant="filled">
            Unable to load Organisations. Please try again or contact AmbuNet
            support.
          </Alert>
        </Grid>
      )}

      {totals && (
        <>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile label="Organisations" value={totals.organisations} />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              label="Journeys this month"
              value={totals.journeysThisMonth}
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              label="Portal bookings"
              value={
                totals.bookings30d
                  ? `${Math.round((totals.portalBookings30d / totals.bookings30d) * 100)}%`
                  : '—'
              }
              caption={`${totals.portalBookings30d}/${totals.bookings30d} last 30 days`}
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              label="Outstanding"
              value={`${currency}${totals.outstandingTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              caption="Approved, sent & overdue invoices"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              label="Active portal users"
              value={`${totals.portalUsersActive30d}/${totals.portalUsersTotal}`}
              caption="Logged in within 30 days"
            />
          </Grid>
          <Grid item xs={6} sm={4} lg={2}>
            <StatTile
              label="Journeys to plan"
              value={totals.unplannedJourneys}
              caption="Due within 14 days"
            />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          display="flex"
          component={Paper}
          padding={2}
        >
          <TextField
            size="small"
            placeholder="Search organisations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="none"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            size="small"
            label="Sort by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            sx={{ minWidth: 180, flex: 0.3 }}
          >
            <MenuItem value="activity">Most active (30d)</MenuItem>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="outstanding">Outstanding balance</MenuItem>
            <MenuItem value="unplanned">Journeys to plan</MenuItem>
          </TextField>
        </Stack>
      </Grid>

      {!loading && organisations.length === 0 && !fetchError && (
        <Grid item xs={12}>
          <Alert severity="info">
            No organisations found
            {search ? ' for this search' : ''}. Use Add New to create one.
          </Alert>
        </Grid>
      )}

      {organisations.map((org) => (
        <Grid
          item
          xs={12}
          md={6}
          xl={4}
          key={org._id}
          display="flex"
        >
          <OrganisationCard
            org={org}
            currency={currency}
            onAnnounce={setAnnounceOrg}
          />
        </Grid>
      ))}

      <BigModal open={openNew} onClose={() => setOpenAddNew(false)}>
        <AddNewClient
          enforceType="PTS"
          customCallbackAction={() => {
            loadDashboard()
            setOpenAddNew(false)
          }}
        />
      </BigModal>
      <AnnouncePortalDialog
        organisation={announceOrg}
        open={!!announceOrg}
        onClose={() => setAnnounceOrg(null)}
      />
    </Grid>
  )
}

export default Organisations
